/**
 * cashierTransport
 *
 * Transport-agnostic dispatcher for the Fiscat cashier payload
 * (`fiscat/AEE|SLD|…`) built by {@link ./FiscatQRService}. Historically the
 * only transport was Bluetooth Classic SPP via `react-native-bluetooth-classic`,
 * which is **Android-only** — iOS/iPad cannot open a Classic SPP link to a
 * non-MFi accessory. This module adds two iOS-capable transports for the very
 * same payload so the cash register works on every platform:
 *
 *   - `bluetooth` – legacy path, Android only (delegates to
 *     `BackgroundTasks.triggerSendToCashier`). On iOS it transparently falls
 *     back to the QR transport so a Bluetooth-configured store still works.
 *   - `wifi`      – push the payload to a WiFi Fiscat over raw TCP
 *     (`react-native-tcp-socket`). The native module is lazily required and
 *     guarded, so a binary that has not yet been rebuilt with the dependency
 *     never crashes — it degrades to QR.
 *   - `qr`        – render the payload as an on-screen QR code that the Fiscat
 *     register scans with its built-in scanner (universal, no pairing/network).
 *
 * The stored `cashierDevice` descriptor determines the transport:
 *   - `{ transport: 'wifi', host, port, name }`         → WiFi (QR fallback)
 *   - `{ transport: 'qr', name }`                        → QR only
 *   - `{ address, name }` / `"AA:BB:…"` (legacy)         → Bluetooth
 *
 * @module cashierTransport
 */

import {Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ErrorReportingService from './ErrorReportingService';
import {showCashRegisterQRModal} from './CashRegisterQRModalService';

/** Supported cashier transports. @enum {string} */
export const CASHIER_TRANSPORT = Object.freeze({
    BLUETOOTH: 'bluetooth',
    WIFI: 'wifi',
    QR: 'qr',
});

/** Default TCP settings for the WiFi transport (all operator-overridable). */
export const WIFI_DEFAULTS = Object.freeze({
    port: 9100,
    timeoutMs: 8000,
    terminator: '\r\n',
});

/**
 * Read and normalise the persisted `cashierDevice` descriptor.
 *
 * Backward compatible with the legacy shapes: a bare MAC string or a
 * `{ address }` object both resolve to the Bluetooth transport.
 *
 * @returns {Promise<?{transport:string, address?:string, host?:string, port?:(number|string), name?:string}>}
 *          The normalised descriptor, or `null` when no cashier is configured.
 */
export const resolveCashierDevice = async () => {
    let raw;
    try {
        raw = await AsyncStorage.getItem('cashierDevice');
    } catch (_error) {
        return null;
    }

    if (!raw) {
        return null;
    }

    let parsed = raw;
    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw);
        } catch (_error) {
            // Legacy installs may store a plain MAC address string.
            parsed = {address: raw};
        }
    }

    if (typeof parsed === 'string') {
        parsed = {address: parsed};
    }

    if (!parsed || typeof parsed !== 'object') {
        return null;
    }

    let transport = parsed.transport;
    if (!transport) {
        if (parsed.host) {
            transport = CASHIER_TRANSPORT.WIFI;
        } else if (parsed.address) {
            transport = CASHIER_TRANSPORT.BLUETOOTH;
        }
    }

    return {...parsed, transport};
};

/**
 * Lazily resolve the optional `react-native-tcp-socket` native module.
 * Returns `null` when the dependency is absent (e.g. the app binary has not
 * been rebuilt yet) so callers can degrade to the QR transport.
 *
 * @returns {?object} The TcpSocket module or `null`.
 */
const loadTcpSocket = () => {
    try {
        const mod = require('react-native-tcp-socket');

        return mod?.default || mod || null;
    } catch (_error) {
        return null;
    }
};

/**
 * Push the Fiscat payload to a WiFi cash register over a raw TCP socket.
 *
 * NOTE: Fiscat's documented order-intake channels are Bluetooth, cable and QR;
 * a TCP listener that accepts the `fiscat/AEE` payload is device/firmware
 * dependent and must be verified against the physical register. This transport
 * is therefore fully configurable (host/port/terminator) and any failure falls
 * back to the guaranteed QR transport by the dispatcher.
 *
 * @param {string} payload - The Fiscat QR payload string.
 * @param {object} config - WiFi target configuration.
 * @param {string} config.host - Register IP / hostname.
 * @param {(number|string)} config.port - TCP port.
 * @param {number} [config.timeoutMs=8000] - Overall connect+write timeout.
 * @param {string} [config.terminator='\r\n'] - Bytes appended after the payload.
 * @param {boolean} [config.expectAck=false] - Resolve on first inbound byte.
 * @returns {Promise<boolean>} Resolves `true` on success; rejects on failure.
 */
export const sendPayloadOverWifi = (payload, config = {}) => new Promise((resolve, reject) => {
    const TcpSocket = loadTcpSocket();
    if (!TcpSocket || typeof TcpSocket.createConnection !== 'function') {
        reject(new Error('WIFI_TRANSPORT_UNAVAILABLE'));

        return;
    }

    const host = config.host;
    const port = Number(config.port || WIFI_DEFAULTS.port);
    const timeoutMs = Number(config.timeoutMs || WIFI_DEFAULTS.timeoutMs);
    const terminator = config.terminator != null ? config.terminator : WIFI_DEFAULTS.terminator;
    const expectAck = Boolean(config.expectAck);

    if (!host || !port) {
        reject(new Error('WIFI_NOT_CONFIGURED'));

        return;
    }

    let settled = false;
    let client = null;
    let timer = null;

    const finish = (error) => {
        if (settled) {
            return;
        }
        settled = true;
        if (timer) {
            globalThis.clearTimeout(timer);
            timer = null;
        }
        try {
            if (client && typeof client.destroy === 'function') {
                client.destroy();
            }
        } catch (_error) { /* ignore – socket already closed */ }

        if (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
        } else {
            resolve(true);
        }
    };

    timer = globalThis.setTimeout(() => finish(new Error('WIFI_TIMEOUT')), timeoutMs);

    try {
        client = TcpSocket.createConnection({host, port, tls: false}, () => {
            try {
                client.write(`${payload}${terminator || ''}`, 'utf8');
            } catch (writeError) {
                finish(writeError);

                return;
            }

            if (!expectAck) {
                // Give the socket a beat to flush the bytes before we close it.
                globalThis.setTimeout(() => finish(null), 400);
            }
        });

        client.on('data', () => {
            if (expectAck) {
                finish(null);
            }
        });
        client.on('error', (socketError) => finish(socketError));
        client.on('close', (hadError) => {
            if (!hadError && !expectAck) {
                finish(null);
            }
        });
    } catch (connectError) {
        finish(connectError);
    }
});

/**
 * Probe a WiFi cash register by opening (and immediately closing) a TCP
 * connection — no fiscal payload is written, so it never prints anything. Used
 * by the settings "Test connection" action.
 *
 * @param {object} config - WiFi target configuration.
 * @param {string} config.host - Register IP / hostname.
 * @param {(number|string)} config.port - TCP port.
 * @param {number} [config.timeoutMs=6000] - Connect timeout.
 * @returns {Promise<boolean>} Resolves `true` when the port accepts the
 *          connection; rejects otherwise.
 */
export const probeCashierWifi = (config = {}) => new Promise((resolve, reject) => {
    const TcpSocket = loadTcpSocket();
    if (!TcpSocket || typeof TcpSocket.createConnection !== 'function') {
        reject(new Error('WIFI_TRANSPORT_UNAVAILABLE'));

        return;
    }

    const host = config.host;
    const port = Number(config.port || WIFI_DEFAULTS.port);
    const timeoutMs = Number(config.timeoutMs || 6000);

    if (!host || !port) {
        reject(new Error('WIFI_NOT_CONFIGURED'));

        return;
    }

    let settled = false;
    let client = null;
    let timer = null;

    const finish = (error) => {
        if (settled) {
            return;
        }
        settled = true;
        if (timer) {
            globalThis.clearTimeout(timer);
            timer = null;
        }
        try {
            if (client && typeof client.destroy === 'function') {
                client.destroy();
            }
        } catch (_error) { /* ignore */ }
        if (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
        } else {
            resolve(true);
        }
    };

    timer = globalThis.setTimeout(() => finish(new Error('WIFI_TIMEOUT')), timeoutMs);

    try {
        client = TcpSocket.createConnection({host, port, tls: false}, () => finish(null));
        client.on('error', (socketError) => finish(socketError));
    } catch (connectError) {
        finish(connectError);
    }
});

/**
 * Present the payload as an on-screen QR code for the register to scan.
 *
 * @param {string} payload - The Fiscat QR payload string.
 * @param {object} [meta] - Display metadata.
 * @param {string} [meta.orderCode] - Order code shown above the QR.
 * @returns {Promise<boolean>} Resolves `true` once the operator confirms the
 *          register scanned it. Rejects with `.deferred = true` when the
 *          operator chose to send it later.
 */
export const sendPayloadOverQr = async (payload, meta = {}) => {
    const confirmed = await showCashRegisterQRModal({
        payload,
        orderCode: meta.orderCode,
    });

    if (confirmed === true) {
        return true;
    }

    const deferredError = new Error('CASHIER_QR_DEFERRED');
    deferredError.deferred = true;

    throw deferredError;
};

/**
 * Dispatch a Fiscat payload over the configured transport, with automatic
 * QR fallback for WiFi failures and for Bluetooth devices on iOS.
 *
 * @param {string} payload - The Fiscat QR payload string.
 * @param {object} [options]
 * @param {string} [options.orderCode] - Order code for QR display / telemetry.
 * @param {object} [options.device] - Pre-resolved descriptor (skips storage read).
 * @returns {Promise<{ok?:boolean, skipped?:boolean, transport?:string, viaFallback?:boolean}>}
 *          Resolves with `skipped: true` when no cashier is configured, or
 *          `ok: true` on a successful send. Rejects on a hard transport error.
 */
export const dispatchCashierPayload = async (payload, options = {}) => {
    const {orderCode} = options;
    const target = options.device || await resolveCashierDevice();

    if (!target || !target.transport) {
        return {skipped: true};
    }

    // WiFi Fiscat on the same LAN — try TCP, fall back to QR on any failure.
    if (target.transport === CASHIER_TRANSPORT.WIFI) {
        try {
            await sendPayloadOverWifi(payload, target);

            return {ok: true, transport: CASHIER_TRANSPORT.WIFI};
        } catch (wifiError) {
            ErrorReportingService.info(
                'WiFi cashier send failed — falling back to on-screen QR',
                {error: String(wifiError?.message || wifiError), orderCode},
                'cashierTransport.dispatchCashierPayload'
            );
            const ok = await sendPayloadOverQr(payload, {orderCode});

            return {ok, transport: CASHIER_TRANSPORT.QR, viaFallback: true};
        }
    }

    // Explicit QR-only cashier.
    if (target.transport === CASHIER_TRANSPORT.QR) {
        const ok = await sendPayloadOverQr(payload, {orderCode});

        return {ok, transport: CASHIER_TRANSPORT.QR};
    }

    // Bluetooth Classic — Android only; iOS degrades to QR.
    if (Platform.OS !== 'android') {
        const ok = await sendPayloadOverQr(payload, {orderCode});

        return {ok, transport: CASHIER_TRANSPORT.QR, viaFallback: true};
    }

    const {triggerSendToCashier} = require('./BackgroundTasks');
    const result = await triggerSendToCashier(payload);

    if (result === true) {
        return {ok: true, transport: CASHIER_TRANSPORT.BLUETOOTH};
    }

    const btError = result?.error instanceof Error
        ? result.error
        : new Error(result?.errorMessage || 'Cashier communication failed');
    btError.stage = result?.stage || 'unknown';

    throw btError;
};
